import {
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  ExclamationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import { Portal } from '@headlessui/react';
import { createPortal } from 'react-dom';
import {
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { OtsActionButton, OtsIconActionButton } from './OtsButton';
import { OtsTimePicker } from './OtsTimePicker';
import { OtsTimeSegmentInput } from './OtsTimeSegmentInput';
import {
  OtsThemeRoot,
  useOtsBrand,
  useOtsColorScheme,
} from '../theme/OtsTheme';

export type OtsDateTimePickerProps = {
  label: ReactNode;
  value?: number;
  onChange: (value?: number) => void;
  description?: ReactNode;
  error?: ReactNode;
  includeTime?: boolean;
  clearable?: boolean;
  required?: boolean;
  disabled?: boolean;
  min?: number;
  max?: number;
  minuteStep?: number;
  size?: 'compact' | 'default';
  placeholder?: string;
  className?: string;
  id?: string;
  /** Keeps the label for assistive technology only, for a picker that sits inline in a toolbar. */
  hideLabel?: boolean;
  /** Shows and edits seconds after the minutes; the picker keeps them when a day or time is chosen. */
  showSeconds?: boolean;
};

const monthFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'long',
  year: 'numeric',
});
const dateFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
});
const shortDateFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'short',
});
const fullDateFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'full',
});
const weekDays = Array.from({ length: 7 }, (_, day) =>
  new Intl.DateTimeFormat(undefined, { weekday: 'narrow' }).format(
    new Date(2024, 0, 7 + day)
  )
);

const startOfDay = (value: Date) =>
  new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
const endOfDay = (value: Date) =>
  new Date(
    value.getFullYear(),
    value.getMonth(),
    value.getDate(),
    23,
    59,
    59,
    999
  ).getTime();
const sameDay = (left: Date, right: Date) =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();
const pad = (value: number) => String(value).padStart(2, '0');
const timeValue = (value?: number) => {
  if (value === undefined) return '00:00';
  const date = new Date(value);
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
const clamp = (value: number, min?: number, max?: number) =>
  Math.min(
    max ?? Number.POSITIVE_INFINITY,
    Math.max(min ?? Number.NEGATIVE_INFINITY, value)
  );
type PickerPosition = Pick<CSSProperties, 'left' | 'top' | 'maxHeight'>;

/** Headless UI reattaches a nested dialog to body when it cannot see it across
 * a shadow boundary. Keep the interactive calendar in its styled shadow root.
 * Outside a shadow root, retain Headless's portal group and focus registration.
 */
const CalendarPortal = ({
  anchor,
  children,
}: {
  anchor: HTMLElement | null;
  children: ReactNode;
}) => {
  const root = anchor?.getRootNode();
  return root instanceof ShadowRoot && anchor?.closest('[role="dialog"]') ? (
    createPortal(children, root)
  ) : (
    <Portal>{children}</Portal>
  );
};

/** A theme-aware local date or date-time selector. Values are epoch milliseconds. */
export const OtsDateTimePicker = ({
  label,
  value,
  onChange,
  description,
  error,
  includeTime = true,
  clearable = true,
  required = false,
  disabled = false,
  min,
  max,
  minuteStep = 1,
  size = 'default',
  placeholder = 'Select date',
  className,
  id,
  hideLabel = false,
  showSeconds = false,
}: OtsDateTimePickerProps) => {
  const isCompact = size === 'compact';
  const generatedId = useId();
  const inputId = id ?? `ots-date-time-${generatedId}`;
  const descriptionId = description ? `${inputId}-description` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const dialogId = `${inputId}-dialog`;
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const brand = useOtsBrand();
  const colorScheme = useOtsColorScheme();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<PickerPosition>({ left: 8, top: 8 });
  const [draft, setDraft] = useState<number | undefined>(value);
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const initial = new Date(value ?? Date.now());
    return new Date(initial.getFullYear(), initial.getMonth(), 1);
  });

  const closePicker = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsidePointer = (event: MouseEvent) => {
      const target = event.target as Node;
      const path = event.composedPath();
      const container = containerRef.current;
      const popup = popupRef.current;
      if (
        !(container && path.includes(container)) &&
        !(popup && path.includes(popup)) &&
        !container?.contains(target) &&
        !popup?.contains(target)
      )
        setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closePicker();
      }
    };
    document.addEventListener('mousedown', closeOnOutsidePointer);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsidePointer);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open, closePicker]);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger || typeof window === 'undefined') return;
    const margin = 8;
    const gap = 8;
    const triggerRect = trigger.getBoundingClientRect();
    const popupRect = popupRef.current?.getBoundingClientRect();
    const popupWidth = Math.min(
      isCompact ? 288 : 336,
      window.innerWidth - margin * 2
    );
    const popupHeight =
      popupRect?.height ||
      Math.min(isCompact ? 360 : 440, window.innerHeight - margin * 2);
    const roomBelow = window.innerHeight - triggerRect.bottom - gap - margin;
    const roomAbove = triggerRect.top - gap - margin;
    const placeBelow =
      roomBelow >= Math.min(popupHeight, 320) || roomBelow >= roomAbove;
    const desiredTop = placeBelow
      ? triggerRect.bottom + gap
      : triggerRect.top - popupHeight - gap;
    setPosition({
      left: Math.max(
        margin,
        Math.min(triggerRect.left, window.innerWidth - popupWidth - margin)
      ),
      top: Math.max(
        margin,
        Math.min(desiredTop, window.innerHeight - popupHeight - margin)
      ),
      maxHeight: window.innerHeight - margin * 2,
    });
  }, [isCompact]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();
    window.addEventListener('resize', updatePosition);
    // Capture scroll from any scrolling table/page ancestor, not only window.
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  const days = useMemo(() => {
    const first = new Date(
      visibleMonth.getFullYear(),
      visibleMonth.getMonth(),
      1
    );
    return Array.from(
      { length: 42 },
      (_, index) =>
        new Date(
          first.getFullYear(),
          first.getMonth(),
          index - first.getDay() + 1
        )
    );
  }, [visibleMonth]);

  const openPicker = () => {
    const initial = new Date(value ?? Date.now());
    setDraft(value);
    setVisibleMonth(new Date(initial.getFullYear(), initial.getMonth(), 1));
    setOpen(true);
  };
  const chooseDay = (day: Date) => {
    const base = new Date(draft ?? Date.now());
    const next = includeTime
      ? new Date(
          day.getFullYear(),
          day.getMonth(),
          day.getDate(),
          base.getHours(),
          base.getMinutes(),
          showSeconds ? base.getSeconds() : 0
        )
      : new Date(day.getFullYear(), day.getMonth(), day.getDate());
    setDraft(clamp(next.getTime(), min, max));
    if (day.getMonth() !== visibleMonth.getMonth()) {
      setVisibleMonth(new Date(day.getFullYear(), day.getMonth(), 1));
    }
  };
  const chooseTime = (nextTime: string) => {
    const [hours, minutes] = nextTime.split(':').map(Number);
    const next = new Date(draft ?? Date.now());
    next.setHours(hours, minutes, showSeconds ? next.getSeconds() : 0, 0);
    setDraft(clamp(next.getTime(), min, max));
  };
  const chooseNow = () => {
    const now = clamp(Date.now(), min, max);
    setDraft(now);
    const date = new Date(now);
    setVisibleMonth(new Date(date.getFullYear(), date.getMonth(), 1));
  };
  const apply = () => {
    if (draft === undefined && required) return;
    onChange(draft);
    closePicker();
  };

  return (
    <div
      ref={containerRef}
      className={['relative', className].filter(Boolean).join(' ')}
    >
      <label
        id={`${inputId}-label`}
        htmlFor={inputId}
        className={
          hideLabel
            ? 'sr-only'
            : [
                'block font-semibold text-[var(--ots-secondary)]',
                size === 'compact' ? 'text-xs' : 'text-sm',
              ].join(' ')
        }
      >
        {label}
        {required && (
          <span aria-hidden="true" className="ml-0.5 text-[var(--ots-error)]">
            *
          </span>
        )}
      </label>
      {/* One field: the date opens the calendar, and with times the hours, minutes, and seconds take typing too. */}
      <div
        className={[
          hideLabel ? '' : 'mt-1.5',
          // The same focus look as the text inputs, shown while the date or any time box has focus.
          '@container flex w-full min-w-0 flex-wrap items-center rounded-lg border bg-[var(--ots-field)] shadow-xs transition-colors focus-within:ring-2',
          // Too narrow for the date and the time side by side, the time wraps under the date rather than either being cut.
          isCompact ? 'min-h-8 gap-x-1 px-2 text-xs' : 'min-h-10 gap-x-1.5 px-3 text-sm',
          disabled ? 'cursor-not-allowed bg-[var(--ots-background)] opacity-60' : '',
          error
            ? 'border-[var(--ots-error)] focus-within:border-[var(--ots-error)] focus-within:ring-[var(--ots-error)]/15'
            : 'border-[var(--ots-field-outline)] hover:border-[var(--ots-outline)] focus-within:border-[var(--ots-primary)] focus-within:ring-[var(--ots-primary-container)]',
        ].join(' ')}
      >
        <button
          ref={triggerRef}
          id={inputId}
          type="button"
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? dialogId : undefined}
          aria-describedby={
            [descriptionId, errorId].filter(Boolean).join(' ') || undefined
          }
          aria-invalid={error ? true : undefined}
          onClick={() => (open ? setOpen(false) : openPicker())}
          className={[
            'flex min-w-0 items-center rounded text-left focus:outline-none focus-visible:underline disabled:cursor-not-allowed',
            includeTime ? 'max-w-full shrink-0' : 'flex-1',
            isCompact ? 'gap-1.5 py-1' : 'gap-2 py-2',
          ].join(' ')}
        >
          <CalendarDaysIcon
            aria-hidden="true"
            className={[
              isCompact ? 'size-4' : 'size-[18px]',
              'shrink-0 text-[var(--ots-primary)]',
            ].join(' ')}
          />
          <span
            className={[
              'truncate',
              value === undefined ? 'text-[var(--ots-text-muted)]' : 'text-[var(--ots-text)]',
            ].join(' ')}
          >
            {value === undefined ? placeholder : <>
              {/* A narrow field shows 10/5/26 rather than cutting "Oct 5, 2026" down to a letter. */}
              <span className={includeTime ? 'hidden @[16rem]:inline' : 'hidden @[10rem]:inline'}>{dateFormatter.format(new Date(value))}</span>
              <span className={includeTime ? '@[16rem]:hidden' : '@[10rem]:hidden'}>{shortDateFormatter.format(new Date(value))}</span>
            </>}
          </span>
        </button>
        {includeTime && (
          <OtsTimeSegmentInput
            label={typeof label === 'string' ? label : 'Time'}
            value={value}
            onChange={onChange}
            showSeconds={showSeconds}
            minuteStep={minuteStep}
            min={min}
            max={max}
            disabled={disabled}
            size={size}
            className="ml-auto shrink-0"
          />
        )}
        {value !== undefined && clearable && !required && !disabled && (
          <button
            type="button"
            aria-label={`Clear ${typeof label === 'string' ? label : 'date'}`}
            onClick={() => onChange(undefined)}
            className={[
              'flex size-7 shrink-0 items-center justify-center rounded text-[var(--ots-text-muted)] hover:bg-[var(--ots-surface-variant)] hover:text-[var(--ots-text)] focus-visible:outline-2 focus-visible:outline-[var(--ots-primary)]',
              includeTime ? '' : 'ml-auto',
              isCompact ? '-mr-1' : '-mr-2',
            ].join(' ')}
          >
            <XMarkIcon aria-hidden="true" className="size-4" />
          </button>
        )}
      </div>

      {description && (
        <p
          id={descriptionId}
          className={[
            'mt-1 leading-5 text-[var(--ots-text-muted)]',
            size === 'compact' ? 'text-[11px]' : 'text-xs',
          ].join(' ')}
        >
          {description}
        </p>
      )}
      {error && (
        <p
          id={errorId}
          className="mt-1.5 flex items-start gap-1 text-xs font-medium text-[var(--ots-error)]"
        >
          <ExclamationCircleIcon
            aria-hidden="true"
            className="mt-px size-3.5 shrink-0"
          />
          {error}
        </p>
      )}

      {open && typeof document !== 'undefined' && (
        <CalendarPortal anchor={triggerRef.current}>
          <OtsThemeRoot
            brand={brand}
            colorScheme={colorScheme}
            defaultColorScheme="system"
            className="contents"
          >
            <div
              ref={popupRef}
              id={dialogId}
              role="dialog"
              aria-modal="false"
              aria-labelledby={`${inputId}-calendar-label`}
              style={{ ...position, pointerEvents: 'auto' }}
              className={[
                'fixed z-[1100] overflow-y-auto border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] text-[var(--ots-text)] shadow-xl',
                isCompact
                  ? 'w-[min(18rem,calc(100vw-1rem))] rounded-lg p-2'
                  : 'w-[min(21rem,calc(100vw-1rem))] rounded-xl p-3',
              ].join(' ')}
            >
              <div
                className={[
                  'flex items-center justify-between',
                  isCompact ? 'gap-1' : 'gap-2',
                ].join(' ')}
              >
                <OtsIconActionButton
                  size="compact"
                  variant="text"
                  icon={<ChevronLeftIcon />}
                  label="Previous month"
                  onClick={() =>
                    setVisibleMonth(
                      (current) =>
                        new Date(
                          current.getFullYear(),
                          current.getMonth() - 1,
                          1
                        )
                    )
                  }
                />
                <p
                  id={`${inputId}-calendar-label`}
                  aria-live="polite"
                  className={[
                    'font-[family-name:var(--ots-font-display)] font-semibold text-[var(--ots-secondary)]',
                    isCompact ? 'text-xs' : 'text-sm',
                  ].join(' ')}
                >
                  {monthFormatter.format(visibleMonth)}
                </p>
                <OtsIconActionButton
                  size="compact"
                  variant="text"
                  icon={<ChevronRightIcon />}
                  label="Next month"
                  onClick={() =>
                    setVisibleMonth(
                      (current) =>
                        new Date(
                          current.getFullYear(),
                          current.getMonth() + 1,
                          1
                        )
                    )
                  }
                />
              </div>

              <div
                className={[
                  'grid grid-cols-7 gap-0.5',
                  isCompact ? 'mt-1' : 'mt-2',
                ].join(' ')}
                role="grid"
                aria-label={monthFormatter.format(visibleMonth)}
              >
                {weekDays.map((day, index) => (
                  <div
                    key={`${day}-${index}`}
                    role="columnheader"
                    aria-label={new Intl.DateTimeFormat(undefined, {
                      weekday: 'long',
                    }).format(new Date(2024, 0, 7 + index))}
                    className={[
                      'text-center font-semibold text-[var(--ots-text-muted)]',
                      isCompact ? 'py-0.5 text-[10px]' : 'py-1 text-[11px]',
                    ].join(' ')}
                  >
                    {day}
                  </div>
                ))}
                {days.map((day) => {
                  const selected =
                    draft !== undefined && sameDay(day, new Date(draft));
                  const today = sameDay(day, new Date());
                  const outsideMonth =
                    day.getMonth() !== visibleMonth.getMonth();
                  const unavailable =
                    (min !== undefined && endOfDay(day) < min) ||
                    (max !== undefined && startOfDay(day) > max);
                  return (
                    <button
                      key={day.toISOString()}
                      type="button"
                      role="gridcell"
                      aria-label={fullDateFormatter.format(day)}
                      aria-pressed={selected}
                      aria-current={today ? 'date' : undefined}
                      disabled={unavailable}
                      onClick={() => chooseDay(day)}
                      className={[
                        'relative flex items-center justify-center transition-colors focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-[var(--ots-primary)] disabled:cursor-not-allowed disabled:opacity-30',
                        isCompact
                          ? 'size-8 rounded-md text-xs'
                          : 'size-10 rounded-lg text-sm',
                        selected
                          ? 'bg-[var(--ots-primary)] font-semibold text-[var(--ots-on-primary)]'
                          : 'hover:bg-[var(--ots-primary-container)] hover:text-[var(--ots-primary)]',
                        !selected && outsideMonth
                          ? 'text-[var(--ots-text-muted)]/60'
                          : '',
                        !selected && !outsideMonth
                          ? 'text-[var(--ots-text)]'
                          : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {day.getDate()}
                      {today && !selected && (
                        <span
                          aria-hidden="true"
                          className="absolute bottom-1 size-1 rounded-full bg-[var(--ots-primary)]"
                        />
                      )}
                    </button>
                  );
                })}
              </div>

              {includeTime && (
                <div
                  className={[
                    'flex items-center border-t border-[var(--ots-outline-variant)] text-xs font-semibold text-[var(--ots-text-muted)]',
                    isCompact ? 'mt-2 gap-1.5 pt-2' : 'mt-3 gap-2 pt-3',
                  ].join(' ')}
                >
                  <ClockIcon
                    aria-hidden="true"
                    className="size-4 text-[var(--ots-primary)]"
                  />
                  Time
                  <OtsTimePicker
                    ariaLabel="Selected time"
                    minuteStep={minuteStep}
                    value={timeValue(draft)}
                    onChange={chooseTime}
                    className="ml-auto"
                  />
                </div>
              )}

              <div
                className={[
                  'flex items-center justify-between gap-2 border-t border-[var(--ots-outline-variant)]',
                  isCompact ? 'mt-2 pt-2' : 'mt-3 pt-3',
                ].join(' ')}
              >
                <div className="flex gap-1">
                  <OtsActionButton
                    size="compact"
                    variant="text"
                    onClick={chooseNow}
                  >
                    {includeTime ? 'Now' : 'Today'}
                  </OtsActionButton>
                  {clearable && !required && (
                    <OtsActionButton
                      size="compact"
                      variant="text"
                      onClick={() => setDraft(undefined)}
                    >
                      Clear
                    </OtsActionButton>
                  )}
                </div>
                <div className="flex gap-2">
                  <OtsActionButton size="compact" onClick={closePicker}>
                    Cancel
                  </OtsActionButton>
                  <OtsActionButton
                    size="compact"
                    variant="primary"
                    disabled={draft === undefined && required}
                    onClick={apply}
                  >
                    Apply
                  </OtsActionButton>
                </div>
              </div>
            </div>
          </OtsThemeRoot>
        </CalendarPortal>
      )}
    </div>
  );
};
